<template>
    <!-- As cards the sections become items of the page's grid, so the root
         adds no box of its own. -->
    <div :class="{ contents: resolvedVariant === 'cards' }">
        <template  v-for="section in visibleSections"
                   :key="section.title"
        >
            <!-- Per-section escape hatch: a report can override just this one
                 section and leave the rest of the schema declarative. -->
            <slot   v-if="$slots[sectionSlotName(section)]"
                    :name="sectionSlotName(section)"
                    :settings="modelValue"
                    :context="context"
            ></slot>

            <report-settings-section    v-else
                                        :title="section.title"
                                        :description="section.description"
                                        :variant="resolvedVariant"
                                        :columns="section.columns ?? 1"
                                        :class="{ 'lg:col-start-2': resolvedVariant === 'cards' && section.cardColumn === 2 }"
            >
                <template   v-for="field in visibleFields(section)"
                            :key="field.key || field.label"
                >
                    <!-- Per-field escape hatch: override the rendering of a single
                         field from the consumer side, without authoring a component
                         or giving up the panel's value syncing. -->
                    <slot   v-if="$slots[fieldSlotName(field)]"
                            :name="fieldSlotName(field)"
                            :field="field"
                            :value="valueFor(field)"
                            :settings="modelValue"
                            :context="fieldContext"
                            :update="(value) => updateField(field, value)"
                    ></slot>

                    <component  v-else
                                :is="componentFor(field)"
                                :model-value="valueFor(field)"
                                :field="fieldProps(field)"
                                :options="optionsFor(field)"
                                :context="fieldContext"
                                :timezone="timezone"
                                :error="errors[field.key]"
                                :variant="resolvedVariant"
                                :class="{ 'col-span-full': field.fullWidth === true }"
                                @update:modelValue="updateField(field, $event)"
                    ></component>
                </template>
            </report-settings-section>
        </template>
    </div>
</template>

<script>
import _ from "lodash"

import ReportSettingsSection from "./ReportSettingsSection.vue"
import {FIELD_TYPES, READ_ONLY_FIELD_TYPES} from "./fieldTypes"

export default {
    name: "ReportSettingsPanel",
    components: {ReportSettingsSection},
    // The page decides the look: the On-Demand form provides 'cards', so every
    // report's settings join its card grid without the report opting in.
    inject: {
        injectedVariant: {from: 'reportSettingsVariant', default: 'list'},
    },
    emits: ['update:modelValue', 'update:interval'],
    props: {
        // The report's settings object. Owned by the parent; this component
        // never mutates it in place, so there is no local mirror to keep in
        // sync and no $forceUpdate needed.
        modelValue: {
            type: Object,
            default: () => ({}),
        },
        schema: {
            type: Object,
            required: true,
        },
        // `interval` and `isSavable` are first-class props rather than context
        // keys so this panel matches the prop/emit contract of the existing
        // *ReportSettings.vue components. That makes a future port a drop-in
        // slot swap needing no GroupReportTemplate.vue changes.
        interval: {
            type: String,
            default: 'monthly',
        },
        isSavable: {
            type: Boolean,
            default: false,
        },
        // Anything else a showIf or custom field needs that is not settings.
        context: {
            type: Object,
            default: () => ({}),
        },
        timezone: {
            type: String,
            default: 'America/New_York',
        },
        errors: {
            type: Object,
            default: () => ({}),
        },
        // 'list' stacks the sections (drawers, template editor); 'cards' makes
        // each section a card. Unset follows the page (see inject).
        variant: {
            type: String,
            default: null,
        },
    },
    computed: {
        resolvedVariant() {
            return this.variant ?? this.injectedVariant
        },
        // Everything a showIf receives: the settings plus the panel's own state.
        conditionScope() {
            return {
                ...this.context,
                settings: this.modelValue,
                interval: this.interval,
                isSavable: this.isSavable,
                variant: this.resolvedVariant,
            }
        },
        // The contract handed to every field component and custom-input escape
        // hatch. Deliberately rich so a bespoke control can reach everything the
        // hand-written settings components could.
        fieldContext() {
            return {
                ...this.conditionScope,
                timezone: this.timezone,
                emit: (event, payload) => this.$emit(event, payload),
            }
        },
        visibleSections() {
            return (this.schema.sections ?? []).filter(section => this.isVisible(section))
        },
    },
    methods: {
        // Kept in sync with schemaVisibility.js's `isVisible`: that module lets a
        // host (GroupReportTemplate) ask whether this schema renders anything in a
        // given context without mounting the panel. Change both together.
        isVisible(entry) {
            if (_.isFunction(entry.showIf) && ! entry.showIf(this.conditionScope)) {
                return false
            }

            // `savableOnly` fields are the settings that only make sense when the
            // panel can persist them (e.g. the automatic sending interval).
            if (entry.savableOnly === true && this.isSavable !== true) {
                return false
            }

            if (! _.isNil(entry.permission) && ! this.can(entry.permission)) {
                return false
            }

            return true
        },
        can(permission) {
            // Permission checking is supplied by the host component so this
            // panel stays free of mixin/global dependencies.
            return _.isFunction(this.context.can)
                ? this.context.can(permission)
                : true
        },
        visibleFields(section) {
            return (section.fields ?? []).filter(field => this.isVisible(field))
        },
        // Resolution order deliberately puts the inline component first: a field
        // can point at a bespoke one-off control with no global registration,
        // which is the escape hatch for genuinely custom report inputs.
        componentFor(field) {
            if (! _.isNil(field.component)) {
                return field.component
            }

            const component = FIELD_TYPES[field.type]
            if (_.isNil(component)) {
                console.error(`Unknown report settings field type: "${field.type}"`, field)
            }
            return component
        },
        isReadOnly(field) {
            return READ_ONLY_FIELD_TYPES.indexOf(field.type) !== -1
        },
        fieldProps(field) {
            // multiselect is select with `multiple` implied.
            return field.type === 'multiselect'
                ? {...field, multiple: true}
                : field
        },
        valueFor(field) {
            // A static field derives its display from the whole settings object
            // rather than owning a key of its own.
            if (field.type === 'static') {
                return _.isFunction(field.value)
                    ? field.value(this.modelValue, this.context)
                    : field.value
            }

            if (this.isReadOnly(field)) {
                return null
            }

            // Some fields are presented in the settings panel but are stored
            // outside report_settings -- `interval` lives on the template row.
            // Those read from the panel's own state rather than the settings.
            if (field.fromContext === true) {
                return this.conditionScope[field.key]
            }

            return this.modelValue[field.key]
        },
        optionsFor(field) {
            return _.isFunction(field.options)
                ? field.options(this.conditionScope)
                : (field.options ?? {})
        },
        updateField(field, value) {
            if (this.isReadOnly(field)) {
                return
            }

            // Fields stored outside report_settings go back to the host through
            // their own event, never into the settings object.
            if (field.fromContext === true) {
                this.$emit(`update:${field.key}`, value)
                return
            }

            // Emit a new object rather than mutating the prop, so parents using
            // v-model get a clean change and deep watchers fire predictably.
            this.$emit('update:modelValue', {...this.modelValue, [field.key]: value})
        },
        sectionSlotName(section) {
            return `section:${_.kebabCase(section.title)}`
        },
        fieldSlotName(field) {
            return `field:${field.key}`
        },
    },
}
</script>
